/**
 * Remote config seguro da Athena.
 *
 * Esta camada somente normaliza dados já obtidos por um cliente autenticado.
 * Ela não faz rede, não usa eval/import dinâmico e não aceita URLs, código,
 * prompts ou seleção de provedores de voz. Assim, uma resposta do servidor
 * nunca pode transformar configuração em execução arbitrária no app.
 */

// Fase 5 Worker contract. Different or absent schemas deliberately resolve to
// fail-closed defaults rather than partially applying a remote response.
export const ATHENA_REMOTE_CONFIG_SCHEMA_VERSION = 2;

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
const isPlainObject = (value) => value !== null
  && typeof value === 'object'
  && (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null);

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  Object.values(value).forEach(deepFreeze);
  return Object.freeze(value);
}

function copyDefaults() {
  return {
    schemaVersion: ATHENA_REMOTE_CONFIG_SCHEMA_VERSION,
    features: { ...ATHENA_REMOTE_CONFIG_DEFAULTS.features },
    voice: { ...ATHENA_REMOTE_CONFIG_DEFAULTS.voice },
    promptBehavior: { ...ATHENA_REMOTE_CONFIG_DEFAULTS.promptBehavior },
    killSwitches: { ...ATHENA_REMOTE_CONFIG_DEFAULTS.killSwitches },
  };
}

/**
 * Valores padrão deliberadamente conservadores. Eles descrevem comportamento,
 * mas não trocam modelos, endpoints, permissões ou a identidade Francisca.
 */
export const ATHENA_REMOTE_CONFIG_DEFAULTS = deepFreeze({
  schemaVersion: ATHENA_REMOTE_CONFIG_SCHEMA_VERSION,
  features: {
    nativeVoiceBeta: false,
    continuousVoiceBeta: false,
    visualCallVoiceBeta: false,
    voiceDiagnosticsPanel: false,
    // Fase 5 surfaces. Every capability remains off until the Worker returns
    // a valid, negotiated flag for this installed client.
    androidWidget: false,
    quickTile: false,
    deviceContext: false,
    automationBeta: false,
    notificationContext: false,
    otaBundle: false,
  },
  voice: {
    manualResultGraceMs: 700,
    finalSettlementMs: 1400,
    finalFallbackMs: 5000,
    continuousMinPauseMs: 5200,
    continuousIncompleteExtensionMs: 6500,
    continuousMaxWaitMs: 14000,
    vadRmsThreshold: 0.018,
  },
  promptBehavior: {
    allowProactiveSuggestions: false,
    allowFollowUpQuestions: true,
    preferStructuredResponses: true,
  },
  // Kill switches only turn a feature off. They never introduce code, URLs,
  // permissions or a new capability from the network.
  killSwitches: {
    deviceContext: false,
    automation: false,
    notificationContext: false,
    otaBundle: false,
  },
});

const BOOLEAN_PATHS = [
  ['features', 'nativeVoiceBeta'],
  ['features', 'continuousVoiceBeta'],
  ['features', 'visualCallVoiceBeta'],
  ['features', 'voiceDiagnosticsPanel'],
  ['features', 'androidWidget'],
  ['features', 'quickTile'],
  ['features', 'deviceContext'],
  ['features', 'automationBeta'],
  ['features', 'notificationContext'],
  ['features', 'otaBundle'],
  ['promptBehavior', 'allowProactiveSuggestions'],
  ['promptBehavior', 'allowFollowUpQuestions'],
  ['promptBehavior', 'preferStructuredResponses'],
  ['killSwitches', 'deviceContext'],
  ['killSwitches', 'automation'],
  ['killSwitches', 'notificationContext'],
  ['killSwitches', 'otaBundle'],
];

const NUMBER_PATHS = [
  ['voice', 'manualResultGraceMs', 200, 5000],
  ['voice', 'finalSettlementMs', 300, 10000],
  ['voice', 'finalFallbackMs', 1000, 30000],
  ['voice', 'continuousMinPauseMs', 1000, 30000],
  ['voice', 'continuousIncompleteExtensionMs', 0, 30000],
  ['voice', 'continuousMaxWaitMs', 3000, 60000],
  ['voice', 'vadRmsThreshold', 0.001, 0.25],
];

function readConfigPayload(payload) {
  if (!isPlainObject(payload)) return { values: null, schemaVersion: null };

  // Aceita tanto { schemaVersion, config } quanto o objeto de configuração
  // diretamente. Em ambos os casos, só os caminhos permitidos abaixo são lidos.
  const values = isPlainObject(payload.config) ? payload.config : payload;
  const flags = isPlainObject(payload.flags)
    ? payload.flags
    : isPlainObject(values.flags) ? values.flags : {};
  // Worker bootstrap uses snake_case flags while UI code uses semantic camel
  // case. Only this fixed mapping crosses that boundary.
  const rawFeatures = isPlainObject(values.features) ? values.features : {};
  const flagValues = { ...rawFeatures, ...flags };
  const flagAliases = {
    androidWidget: flagValues.android_widget,
    quickTile: flagValues.quick_tile,
    deviceContext: flagValues.device_context,
    automationBeta: flagValues.automation_beta,
    notificationContext: flagValues.notification_context,
    otaBundle: flagValues.ota_bundle,
  };
  const mappedFeatures = { ...rawFeatures };
  Object.entries(flagAliases).forEach(([key, value]) => {
    if (typeof value === 'boolean') mappedFeatures[key] = value;
  });
  const mappedKillSwitches = isPlainObject(values.killSwitches)
    ? values.killSwitches
    : isPlainObject(values.kill_switches) ? values.kill_switches : {};
  const normalizedValues = {
    ...values,
    features: mappedFeatures,
    killSwitches: mappedKillSwitches,
  };
  const schemaVersion = payload.schemaVersion ?? values.schemaVersion ?? null;
  return { values: normalizedValues, schemaVersion };
}

function knownPaths(source) {
  if (!isPlainObject(source)) return [];
  const paths = [];
  for (const [group, fields] of Object.entries(source)) {
    if (!isPlainObject(fields)) continue;
    for (const field of Object.keys(fields)) paths.push(`${group}.${field}`);
  }
  return paths;
}

/**
 * Valida e aplica apenas a allowlist de valores não sensíveis.
 *
 * O retorno inclui metadados sem os valores rejeitados, para que o suporte
 * consiga entender uma configuração malformada sem registrar conteúdo remoto.
 */
export function resolveAthenaRemoteConfig(payload) {
  const config = copyDefaults();
  const acceptedKeys = [];
  const ignoredKeys = [];
  const { values, schemaVersion } = readConfigPayload(payload);

  if (!values) {
    return {
      config: deepFreeze(config),
      acceptedKeys,
      ignoredKeys: ['payload'],
      source: 'defaults',
      schemaValid: false,
    };
  }

  if (schemaVersion !== null && schemaVersion !== ATHENA_REMOTE_CONFIG_SCHEMA_VERSION) {
    return {
      config: deepFreeze(config),
      acceptedKeys,
      ignoredKeys: ['schemaVersion'],
      source: 'defaults',
      schemaValid: false,
    };
  }

  const allowedPaths = new Set([
    ...BOOLEAN_PATHS.map(([group, key]) => `${group}.${key}`),
    ...NUMBER_PATHS.map(([group, key]) => `${group}.${key}`),
    'schemaVersion',
  ]);

  for (const path of knownPaths(values)) {
    if (!allowedPaths.has(path)) ignoredKeys.push(path);
  }

  for (const [group, key] of BOOLEAN_PATHS) {
    const groupValue = values[group];
    if (!isPlainObject(groupValue) || !hasOwn(groupValue, key)) continue;
    if (typeof groupValue[key] === 'boolean') {
      config[group][key] = groupValue[key];
      acceptedKeys.push(`${group}.${key}`);
    } else {
      ignoredKeys.push(`${group}.${key}`);
    }
  }

  for (const [group, key, minimum, maximum] of NUMBER_PATHS) {
    const groupValue = values[group];
    if (!isPlainObject(groupValue) || !hasOwn(groupValue, key)) continue;
    const candidate = groupValue[key];
    if (typeof candidate === 'number' && Number.isFinite(candidate)
      && candidate >= minimum && candidate <= maximum) {
      config[group][key] = candidate;
      acceptedKeys.push(`${group}.${key}`);
    } else {
      ignoredKeys.push(`${group}.${key}`);
    }
  }

  return {
    config: deepFreeze(config),
    acceptedKeys: Object.freeze([...new Set(acceptedKeys)]),
    ignoredKeys: Object.freeze([...new Set(ignoredKeys)].slice(0, 40)),
    source: acceptedKeys.length > 0 ? 'remote' : 'defaults',
    schemaValid: true,
  };
}

/**
 * Atalho para consumidores que só precisam da configuração efetiva.
 * Não há rede e não há possibilidade de executar dados recebidos do servidor.
 */
export function mergeAthenaRemoteConfig(payload) {
  return resolveAthenaRemoteConfig(payload).config;
}

export const ATHENA_REMOTE_FEATURES = Object.freeze(Object.keys(ATHENA_REMOTE_CONFIG_DEFAULTS.features));

/**
 * Capability surfaces call this instead of reading raw flags. A kill switch
 * has priority and unknown keys are always unavailable.
 */
export function isAthenaRemoteFeatureEnabled(config, feature) {
  if (!ATHENA_REMOTE_FEATURES.includes(feature)) return false;
  const features = config?.features || {};
  const kills = config?.killSwitches || {};
  if (feature === 'deviceContext' && kills.deviceContext === true) return false;
  if (feature === 'automationBeta' && kills.automation === true) return false;
  if (feature === 'notificationContext' && kills.notificationContext === true) return false;
  if (feature === 'otaBundle' && kills.otaBundle === true) return false;
  return features[feature] === true;
}
